import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { EditorHero, MonoLabel, SectionCap, EditorialButton, Field } from '../components/Editorial';

/**
 * Guardian "Connect your athlete" — two-step phone-OTP flow that links a
 * signed-in guardian to an athlete registered at one of our camps, without
 * requiring an email-based guardian invite.
 *
 * Step 1 (phone): POST /guardians/connect/send-code {phone} -> sets `masked`
 *   and advances to step 'code'.
 * Step 2 (code):  POST /guardians/connect/verify {code} -> on success sets
 *   `linked` ([{ athlete_user_id, athlete_name }]) and shows a confirmation.
 *
 * Auth is the global axios default Authorization header set by AuthContext
 * (no per-call config needed) — same pattern as GuardianManagePage.
 */

const PHONE_ERROR_MESSAGES = {
  invalid_phone: 'Enter a valid 10-digit US phone number.',
};

const VERIFY_ERROR_MESSAGES = {
  invalid_code: "That code didn't match — try again.",
  code_expired: 'Code expired — resend a new one.',
  too_many_attempts: 'Too many tries — start over.',
  no_match: "We couldn't find a camp athlete linked to that number.",
};

export default function ConnectAthletePage() {
  const { API_URL } = useAuth();
  const navigate = useNavigate();

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' | 'code'
  const [masked, setMasked] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [linked, setLinked] = useState(null);

  const sendCode = async () => {
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/guardians/connect/send-code`, { phone });
      setMasked(data.phone_masked || '');
      setCode('');
      setStep('code');
    } catch (err) {
      const errCode = err.response?.data?.detail?.code;
      setError(PHONE_ERROR_MESSAGES[errCode] || 'Could not send a code — please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = (e) => {
    e.preventDefault();
    if (!phone || loading) return;
    sendCode();
  };

  const handleResend = () => {
    if (loading) return;
    sendCode();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code || loading) return;
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/guardians/connect/verify`, { code });
      setLinked(data.linked || []);
    } catch (err) {
      const errCode = err.response?.data?.detail?.code;
      setError(VERIFY_ERROR_MESSAGES[errCode] || 'Could not verify that code — please try again.');
      if (errCode === 'too_many_attempts') {
        // Too many failed attempts — send them back to the start rather
        // than let them keep hammering the same (now-locked) code.
        setStep('phone');
        setCode('');
        setMasked('');
      }
    } finally {
      setLoading(false);
    }
  };

  if (linked) {
    const names = linked.map((a) => a.athlete_name).filter(Boolean).join(', ');
    const summary = linked.length > 0
      ? `Connected to ${names || `${linked.length} athlete${linked.length === 1 ? '' : 's'}`}.`
      : "You're connected — no new athletes were linked.";

    return (
      <div className="ed-page">
        <EditorHero
          eyebrow={<MonoLabel onDark>GUARDIAN &middot; CONNECT ATHLETE</MonoLabel>}
          headline="You're"
          accent="connected."
          sub={summary}
        />
        <div className="ed-card">
          <div className="auth-success" role="status">{summary}</div>
          <div className="ed-row" style={{ marginTop: 16 }}>
            <EditorialButton variant="solid" onClick={() => navigate('/dashboard')}>
              Go to dashboard
            </EditorialButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ed-page">
      <EditorHero
        eyebrow={<MonoLabel onDark>GUARDIAN &middot; CONNECT ATHLETE</MonoLabel>}
        headline="Connect your"
        accent="athlete."
        sub="Link your account to an athlete registered at one of our camps using their phone number."
      />

      <SectionCap
        label={step === 'phone' ? 'Step 1 of 2' : 'Step 2 of 2'}
        hint={step === 'phone' ? 'ENTER PHONE' : 'ENTER CODE'}
      />

      <div className="ed-card">
        {error && <div className="auth-error" role="alert">{error}</div>}

        {step === 'phone' && (
          <form className="auth-form" onSubmit={handleSendCode}>
            <Field label="Athlete's phone number">
              <input
                id="connect-phone"
                type="tel"
                className="ed-field__input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                autoComplete="tel"
                required
              />
            </Field>
            <div className="ed-row" style={{ marginTop: 12 }}>
              <EditorialButton variant="solid" type="submit" disabled={loading || !phone}>
                {loading ? 'Sending…' : 'Send code'}
              </EditorialButton>
            </div>
          </form>
        )}

        {step === 'code' && (
          <form className="auth-form" onSubmit={handleVerify}>
            <p style={{ fontSize: 14, color: 'var(--ed-color-muted, #64748b)', margin: '0 0 4px' }}>
              {masked ? `We sent a code to ${masked}.` : 'We sent a code to that number.'}
            </p>
            <Field label="Verification code">
              <input
                id="connect-code"
                type="text"
                inputMode="numeric"
                className="ed-field__input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                autoComplete="one-time-code"
                required
              />
            </Field>
            <div className="ed-row" style={{ marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
              <EditorialButton variant="solid" type="submit" disabled={loading || !code}>
                {loading ? 'Verifying…' : 'Verify & connect'}
              </EditorialButton>
              <EditorialButton variant="ghost" type="button" onClick={handleResend} disabled={loading}>
                Resend code
              </EditorialButton>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
